import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLocale } from "../../../context/LocaleContext";

function AccordionRow({ id, title, isOpen, onToggle, paragraphs = [] }) {
  return (
    <div className="border-b border-white/10 pb-3">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 py-2 text-left"
        onClick={() => onToggle(isOpen ? null : id)}
      >
        <span className="pr-2 font-semibold">{title}</span>
        <span className="flex flex-shrink-0 items-center text-white/60">
          <img
            src="/assets/search/arrow_down.svg"
            alt=""
            className={isOpen ? "h-4 w-4 rotate-180 transition-transform" : "h-4 w-4 transition-transform"}
          />
        </span>
      </button>

      {isOpen ? (
        <div className="mt-2 space-y-2 pl-2 text-[15px] text-white/90">
          {paragraphs.map((paragraph, index) => (
            <p key={`${id}-${index}`}>{paragraph}</p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function LandingInfoContent({
  activeKey,
  variant = "page",
  className = "",
}) {
  const { t } = useTranslation("about");
  const { isRussianContour } = useLocale();
  const [openFaq, setOpenFaq] = useState(null);
  const [openRule, setOpenRule] = useState(null);
  const [openAbout, setOpenAbout] = useState(null);

  useEffect(() => {
    setOpenFaq(null);
    setOpenRule(null);
    setOpenAbout(null);
  }, [activeKey]);

  const isLandingVariant = variant === "landing";
  const contentWidthClass = isLandingVariant
    ? "w-full"
    : "mx-auto w-full max-w-sm md:max-w-[900px] lg:max-w-[1100px]";
  const titleClassName = isLandingVariant
    ? "mb-6 text-center text-[clamp(24px,8.5vw,38px)] font-extrabold leading-[1.08]"
    : "mb-6 text-center text-3xl font-extrabold leading-tight";
  const textBlockClassName = isLandingVariant
    ? "space-y-5 text-left text-[17px] leading-[1.72] text-white/92"
    : "space-y-4 text-left text-base leading-relaxed";

  const contactsData = useMemo(() => {
    const body = t("contacts.body");
    const paragraphs = body ? body.split(/\n\s*\n/) : [];
    const organizerIndex = paragraphs.findIndex((paragraph) => paragraph.trim().startsWith("1."));
    const venueIndex = paragraphs.findIndex((paragraph) => paragraph.trim().startsWith("2."));

    return {
      intro: organizerIndex > -1 ? paragraphs.slice(0, organizerIndex) : paragraphs,
      items: [
        {
          id: "contact-organizers",
          title:
            organizerIndex > -1 ? paragraphs[organizerIndex].trim() : "1. Partnership for event organizers",
          body:
            organizerIndex > -1
              ? paragraphs.slice(organizerIndex + 1, venueIndex > -1 ? venueIndex : paragraphs.length)
              : [],
        },
        {
          id: "contact-venues",
          title:
            venueIndex > -1 ? paragraphs[venueIndex].trim() : "2. Partnership with venues and local businesses",
          body: venueIndex > -1 ? paragraphs.slice(venueIndex + 1) : [],
        },
      ],
    };
  }, [t]);

  if (activeKey === "about") {
    const intro = t("about.intro", { returnObjects: true }) || [];
    const sections = t("about.sections", { returnObjects: true }) || [];
    const values = t("about.values", { returnObjects: true }) || [];

    return (
      <div className={`${contentWidthClass} ${className}`}>
        <h1 className={titleClassName}>
          {t("about.hero_title", { defaultValue: t("seo.title") })}
        </h1>

        {intro.length > 0 ? (
          <div className={textBlockClassName}>
            {intro.map((paragraph, index) => (
              <p key={`about-intro-${index}`}>{paragraph}</p>
            ))}
          </div>
        ) : null}

        {sections.length > 0 ? (
          <div className="mt-6 space-y-4 text-left">
            {sections.map((section, index) => {
              const id = `about-section-${index}`;
              return (
                <AccordionRow
                  key={id}
                  id={id}
                  title={section.title}
                  isOpen={openAbout === id}
                  onToggle={setOpenAbout}
                  paragraphs={Array.isArray(section.body) ? section.body : []}
                />
              );
            })}
          </div>
        ) : null}

        {values.length > 0 ? (
          <div className="mt-6 text-left">
            <h2 className="mb-2 text-lg font-extrabold">{t("about.values_title")}</h2>
            <ul className="list-inside list-disc space-y-1 text-base leading-relaxed">
              {values.map((value, index) => (
                <li key={`about-value-${index}`}>{value}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {isRussianContour ? (
          <section
            className="mt-8 rounded-2xl border border-white/15 bg-white/[0.06] p-5 text-left"
            data-testid="rf-legal-information"
          >
            <h2 className="text-lg font-extrabold">Документы и реквизиты российского сервиса</h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80">
              Российский контур VaultGrid обслуживает ООО «ВОЛГРИД ВОСТОК».
            </p>
            <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm leading-relaxed text-white/90 sm:grid-cols-[auto_1fr]">
              <dt className="font-semibold text-white">ОГРН</dt>
              <dd>1269600028268</dd>
              <dt className="font-semibold text-white">ИНН</dt>
              <dd>6686174440</dd>
              <dt className="font-semibold text-white">КПП</dt>
              <dd>668601001</dd>
              <dt className="font-semibold text-white">Юридический адрес</dt>
              <dd>624090, Российская Федерация, Свердловская область, г. Верхняя Пышма, ул. Орджоникидзе, д. 9, кв. 142</dd>
              <dt className="font-semibold text-white">Телефон</dt>
              <dd>
                <a className="underline underline-offset-2" href="tel:+79292182516">
                  +7 (929) 218-25-16
                </a>
              </dd>
              <dt className="font-semibold text-white">Поддержка</dt>
              <dd>
                <a className="break-all underline underline-offset-2" href="mailto:support@vaultgrid.net">
                  support@vaultgrid.net
                </a>
              </dd>
              <dt className="font-semibold text-white">Персональные данные</dt>
              <dd>
                <a className="break-all underline underline-offset-2" href="mailto:privacy@vaultgrid.net">
                  privacy@vaultgrid.net
                </a>
              </dd>
            </dl>

            <nav className="mt-5 flex flex-col items-start gap-3 text-sm font-semibold" aria-label="Юридические документы российского сервиса">
              <Link className="underline underline-offset-4" to="/legal/user-agreement?from=about">
                Пользовательское соглашение
              </Link>
              <Link className="underline underline-offset-4" to="/legal/terms?from=about">
                Публичная оферта
              </Link>
              <Link className="underline underline-offset-4" to="/legal/privacy?from=about">
                Политика обработки персональных данных
              </Link>
            </nav>
          </section>
        ) : null}
      </div>
    );
  }

  if (activeKey === "faq") {
    const items = t("faq.items", { returnObjects: true }) || [];

    return (
      <div className={`${contentWidthClass} ${className}`}>
        <h1 className={titleClassName}>{t("faq.title")}</h1>

        <div className="space-y-4 text-left">
          {items.map((item, index) => {
            const id = `faq-${index}`;
            const rawAnswer = item.a;
            const answerParts = Array.isArray(rawAnswer)
              ? rawAnswer
              : typeof rawAnswer === "string"
                ? rawAnswer
                    .split("•")
                    .map((segment) => segment.trim())
                    .filter(Boolean)
                : [];

            return (
              <AccordionRow
                key={id}
                id={id}
                title={item.q}
                isOpen={openFaq === id}
                onToggle={setOpenFaq}
                paragraphs={answerParts.map((paragraph) =>
                  answerParts.length > 1 ? `• ${paragraph}` : paragraph
                )}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (activeKey === "rules") {
    const intro = t("rules.intro", { returnObjects: true }) || [];
    const items = t("rules.items", { returnObjects: true }) || [];

    return (
      <div className={`${contentWidthClass} ${className}`}>
        <h1 className={titleClassName}>{t("rules.title")}</h1>

        {intro.length > 0 ? (
          <div className={`${textBlockClassName} mb-4`}>
            {intro.map((paragraph, index) => (
              <p key={`rules-intro-${index}`}>{paragraph}</p>
            ))}
          </div>
        ) : null}

        <div className="space-y-4 text-left">
          {items.map((item, index) => {
            const id = `rule-${index}`;
            const bodyParts = Array.isArray(item.body) ? item.body : [];
            const bulletParts = Array.isArray(item.bullets) ? item.bullets : [];

            return (
              <AccordionRow
                key={id}
                id={id}
                title={item.title}
                isOpen={openRule === id}
                onToggle={setOpenRule}
                paragraphs={[
                  ...bodyParts,
                  ...bulletParts.map((bullet) => `• ${bullet}`),
                ]}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (activeKey === "contacts") {
    return (
      <div className={`${contentWidthClass} ${className}`}>
        <h1 className={titleClassName}>{t("contacts.title")}</h1>

        {contactsData.intro.length > 0 ? (
          <div className={`${textBlockClassName} mb-4 whitespace-pre-line`}>
            {contactsData.intro.map((paragraph, index) => (
              <p key={`contacts-intro-${index}`}>{paragraph}</p>
            ))}
          </div>
        ) : null}

        <div className="space-y-4 text-left">
          {contactsData.items.map((item) => (
            <AccordionRow
              key={item.id}
              id={item.id}
              title={item.title}
              isOpen={openRule === item.id}
              onToggle={setOpenRule}
              paragraphs={item.body}
            />
          ))}
        </div>
      </div>
    );
  }

  return null;
}
